import React from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  AlertCircle, 
  MessageCircle, 
  Car, 
  Truck
} from "lucide-react";
import {
  SERVICE_TIERS,
  TOUCH_UPS,
  PANEL_PRICING,
  whatsappLink,
} from "@/lib/business";
import SectionHeader from "@/components/SectionHeader";
import {
  BonnetIcon,
  RoofIcon,
  BumperIcon,
  DoorIcon,
  FenderIcon,
  QuarterPanelIcon,
  HeadlightIcon,
  TrunkIcon
} from "@/components/CarPartIcons";

// Car part image URLs - actual photos of car parts
const PANEL_IMAGES = {
  "Bonnet": "https://images.pexels.com/photos/16070478/pexels-photo-16070478.jpeg?auto=compress&cs=tinysrgb&w=200",
  "Roof": "https://images.pexels.com/photos/16070478/pexels-photo-16070478.jpeg?auto=compress&cs=tinysrgb&w=200",
  "Bumper (ea F/B)": "https://images.unsplash.com/photo-1657638005567-6ca7f65c4de8?w=200&q=80",
  "Door (ea)": "https://images.pexels.com/photos/16070478/pexels-photo-16070478.jpeg?auto=compress&cs=tinysrgb&w=200",
  "Fender (ea F/B)": "https://images.unsplash.com/photo-1711462245183-69171c67f133?w=200&q=80",
  "Quarter Panel (ea)": "https://images.unsplash.com/photo-1711462245183-69171c67f133?w=200&q=80",
  "Head Light": "https://images.unsplash.com/photo-1587668635372-66498d7a261b?w=200&q=80",
  "Trunk / Tailgate": "https://images.pexels.com/photos/16070478/pexels-photo-16070478.jpeg?auto=compress&cs=tinysrgb&w=200",
};

const VEHICLE_META = {
  Standard: {
    accent: "#FFCC00",
    icon: Car,
    subtitle: "Sedans • Hatchbacks • Coupes",
  },
  SUV: {
    accent: "#E60000",
    icon: Car,
    subtitle: "SUVs • Crossovers • MPVs",
  },
  "Pick-up Truck": {
    accent: "#FFFFFF",
    icon: Truck,
    subtitle: "Pick-ups • Utility Trucks",
  },
};

const PanelPricingGrid = () => (
  <div
    className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5"
    data-testid="panel-pricing-grid"
  >
    {PANEL_PRICING.rows.map((row) => {
      const meta = VEHICLE_META[row.type] || { accent: "#E60000", icon: Car, subtitle: "" };
      const Icon = meta.icon;
      return (
        <div
          key={row.type}
          className="border border-[#27272A] bg-[#0a0a0a] hover:bg-[#111] transition-colors overflow-hidden"
          data-testid={`panel-card-${row.type.toLowerCase().replace(/\s+/g, "-")}`}
        >
          {/* Header band */}
          <div
            className="p-5 md:p-6 flex items-center gap-4 border-b border-[#27272A]"
            style={{
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0))",
            }}
          >
            <span
              className="inline-flex items-center justify-center w-12 h-12"
              style={{ background: meta.accent, color: meta.accent === "#FFCC00" || meta.accent === "#FFFFFF" ? "#000" : "#fff" }}
            >
              <Icon className="w-6 h-6" strokeWidth={2.25} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-display text-2xl md:text-3xl text-white tracking-wide leading-none">
                {row.type.toUpperCase()}
              </div>
              <div className="mt-1.5 text-xs text-[#d4d4d8] font-body">
                {meta.subtitle}
              </div>
            </div>
          </div>

          {/* Panel rows */}
          <ul className="divide-y divide-[#27272A]">
            {PANEL_PRICING.panels.map((panel, i) => {
              const isRear = i === PANEL_PRICING.panels.length - 1;
              const label = isRear ? row.rearLabel : panel;
              const imageUrl = PANEL_IMAGES[panel];
              return (
                <li
                  key={panel}
                  className="flex items-center justify-between gap-4 px-5 md:px-6 py-4"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div 
                      className="w-12 h-12 rounded overflow-hidden shrink-0 border border-[#27272A] bg-[#1a1a1a]"
                    >
                      <img 
                        src={imageUrl} 
                        alt={panel}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="min-w-0">
                      <div className="font-body text-sm md:text-base text-white leading-tight">
                        {isRear ? label : panel.replace(" (ea F/B)", "").replace(" (ea)", "")}
                      </div>
                      {(isRear || panel.includes("(ea")) && (
                        <div className="text-[10px] uppercase tracking-widest text-[#FFCC00] font-display mt-1">
                          {isRear
                            ? "Rear Panel"
                            : panel.includes("(ea F/B)")
                            ? "Each (Front or Back)"
                            : "Per Panel"}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="font-display text-xl md:text-2xl text-white whitespace-nowrap">
                    {row.values[i]}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      );
    })}
  </div>
);

export const Pricing = () => {
  return (
    <div data-testid="pricing-page" className="bg-[#050505]">
      {/* Hero */}
      <section className="relative pt-16 pb-20 md:pt-24 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="font-display text-xs tracking-[0.4em] text-[#FFCC00]">
            / PRICING
          </div>
          <h1 className="mt-4 font-display text-5xl md:text-7xl lg:text-8xl text-white leading-[0.9]">
            STRAIGHT
            <br />
            <span className="text-[#E60000]">PRICING.</span>
            <br />
            NO GAMES.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#d4d4d8] leading-relaxed">
            Transparent estimates for every service tier and panel. Final pricing
            confirmed after physical inspection.
          </p>
        </div>
      </section>

      {/* Service tier prices */}
      <section className="pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <SectionHeader
            kicker="FULL PAINT JOBS"
            title="SERVICE TIERS."
            description="Single-stage and basecoat/clearcoat options for every condition and budget."
          />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#27272A] border border-[#27272A]">
            {SERVICE_TIERS.map((t) => (
              <div
                key={t.id}
                className="bg-[#0a0a0a] p-8 relative hover:bg-[#111] transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div
                      className={`font-display text-xs tracking-[0.3em] ${
                        t.accent === "red" ? "text-[#E60000]" : "text-[#FFCC00]"
                      }`}
                    >
                      {t.eta}
                    </div>
                    <h3 className="mt-2 font-display text-2xl text-white leading-tight">
                      {t.title}
                    </h3>
                  </div>
                  <div className="font-display text-2xl text-white whitespace-nowrap">
                    {t.price}
                  </div>
                </div>
                <p className="mt-4 text-sm text-[#d4d4d8]">{t.blurb}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Panel pricing tables */}
      <section className="bg-black py-24 border-t border-[#27272A]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <SectionHeader
            kicker="PANEL PRICING"
            title="PER-PANEL ESTIMATES."
            description="Standard, SUV, and Truck pricing across all common body panels."
          />
          <div className="mt-10">
            <PanelPricingGrid />
          </div>
          <p className="mt-5 text-sm text-[#d4d4d8] font-body">
            <span className="text-[#FFCC00] font-display tracking-widest text-xs mr-2">NOTE:</span>
            {PANEL_PRICING.footnote}
          </p>

          {/* Touch-up examples */}
          <div className="mt-16">
            <h3 className="font-display text-2xl text-white mb-6">
              TOUCH-UP EXAMPLES
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#27272A] border border-[#27272A]">
              {TOUCH_UPS.map((group) => (
                <div key={group.title} className="bg-[#0a0a0a] p-8">
                  <h4 className="font-display text-xl text-white">
                    {group.title}
                  </h4>
                  <div className="mt-4 space-y-3">
                    {group.examples.map((ex) => (
                      <div
                        key={ex.vehicle}
                        className="flex items-start justify-between gap-3 border-b border-[#27272A] pb-3"
                      >
                        <div>
                          <div className="font-display text-base text-white">
                            {ex.vehicle}
                          </div>
                          <div className="text-xs text-[#d4d4d8]">{ex.note}</div>
                        </div>
                        <div className="font-display text-xl text-[#FFCC00]">
                          {ex.price}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="border border-[#FFCC00]/40 bg-[#1a1500]/30 p-6 md:p-8 flex gap-4 items-start">
            <AlertCircle className="w-6 h-6 text-[#FFCC00] mt-0.5 shrink-0" />
            <div className="text-[#e4e4e7] text-sm md:text-base leading-relaxed">
              <span className="font-display text-[#FFCC00] tracking-widest text-xs block mb-2">
                IMPORTANT NOTES
              </span>
              Final pricing may vary based on vehicle size, paint type, color
              match difficulty, extent of repairs, and rust severity. Hidden
              damage may affect final labor and material requirements. All
              estimates are subject to physical inspection. Insurance-related
              work can be documented with repair photos and itemized scope on
              request.
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0a0a0a] border-t border-[#27272A] py-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row gap-6 items-start md:items-center justify-between">
          <div>
            <div className="font-display text-xs tracking-[0.4em] text-[#FFCC00] mb-2">
              GET ACCURATE PRICING
            </div>
            <h3 className="font-display text-3xl md:text-4xl text-white">
              SEND PHOTOS. <span className="text-[#E60000]">GET A QUOTE.</span>
            </h3>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="pricing-cta-quote"
              className="inline-flex items-center gap-3 bg-[#FFCC00] hover:bg-white text-black font-display tracking-widest text-sm px-8 py-4 transition-all hover:-translate-y-1"
            >
              REQUEST QUOTE <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pricing-cta-whatsapp"
              className="inline-flex items-center gap-3 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-display tracking-widest text-sm px-8 py-4 transition-all hover:-translate-y-1"
            >
              <MessageCircle className="w-4 h-4" /> WHATSAPP
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
